import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCw, MoreVertical, Pencil, Copy, Pause, Play, Trash2, BarChart3, Download, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import QRPreview from "../components/QRPreview";
import QRCodeLib from "qrcode";
import { api, scanUrl } from "../api";
import { useToast } from "../hooks/use-toast";
import { COLOR_PRESETS } from "../mock";

const TARGETS = [
  { id: "url", label: "Link esterno (URL)" },
  { id: "page", label: "Pagina Qora" },
  { id: "menu", label: "Menù digitale" },
];

export default function QRManager() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [qr, pg, mn] = await Promise.all([api.qrList(), api.pageList(), api.menuList()]);
      setItems(qr.data); setPages(pg.data); setMenus(mn.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditor({ name: "", dynamic: true, target_type: "url", target_value: "", style: { fg: "#123C46", bg: "#ffffff" } });
  const openEdit = (q) => setEditor({ ...q });

  const save = async () => {
    if (!editor.name.trim()) { toast({ title: "Dai un nome al QR" }); return; }
    try {
      if (editor.id) {
        await api.qrUpdate(editor.id, { name: editor.name, target_type: editor.target_type, target_value: editor.target_value, style: editor.style });
        toast({ title: "QR aggiornato", description: editor.dynamic ? "La destinazione è cambiata, lo stesso codice resta valido." : "" });
      } else {
        await api.qrCreate(editor);
        toast({ title: "QR creato" });
      }
      setEditor(null); load();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
  };

  const toggle = async (q) => { await api.qrUpdate(q.id, { status: q.status === "active" ? "paused" : "active" }); load(); };
  const dup = async (q) => { await api.qrDuplicate(q.id); toast({ title: "QR duplicato" }); load(); };
  const del = async (q) => { await api.qrDelete(q.id); toast({ title: "QR archiviato" }); load(); };

  const valueFor = (q) => q.dynamic ? scanUrl(q.code) : (q.target_value || "https://qora.app");

  const downloadPng = (q) => {
    const c = document.getElementById(`qr-${q.id}`)?.querySelector("canvas");
    if (!c) return;
    const a = document.createElement("a"); a.download = `${q.name}.png`; a.href = c.toDataURL("image/png"); a.click();
  };

  const downloadSvg = async (q) => {
    try {
      const svg = await QRCodeLib.toString(valueFor(q), { type: "svg", margin: 1, color: { dark: q.style?.fg || "#0B1120", light: q.style?.bg || "#ffffff" } });
      const blob = new Blob([svg], { type: "image/svg+xml" });
      const a = document.createElement("a"); a.download = `${q.name}.svg`; a.href = URL.createObjectURL(blob); a.click();
      toast({ title: "SVG scaricato", description: "Formato vettoriale, ideale per la stampa." });
    } catch { toast({ title: "Errore export SVG", variant: "destructive" }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold">QR Code</h1>
          <p className="text-muted-foreground mt-1">Crea codici dinamici e cambia la destinazione quando vuoi.</p>
        </div>
        <Button className="btn-grad text-white font-semibold gap-2" onClick={openNew}><Plus className="h-4 w-4" /> Nuovo QR</Button>
      </div>

      {loading ? <div className="text-muted-foreground">Caricamento…</div> : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <p className="font-semibold">Nessun QR ancora</p>
          <p className="text-muted-foreground text-sm mt-1">Crea il tuo primo codice dinamico.</p>
          <Button className="btn-grad text-white font-semibold gap-2 mt-4" onClick={openNew}><Plus className="h-4 w-4" /> Nuovo QR</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((q) => (
            <div key={q.id} className="rounded-2xl border border-border bg-white p-4 card-shadow">
              <div className="flex items-start justify-between">
                <div id={`qr-${q.id}`} className="rounded-xl border border-border p-2"><QRPreview value={valueFor(q)} fgColor={q.style?.fg || "#0B1120"} bgColor={q.style?.bg || "#fff"} size={96} frame="none" /></div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(q)}><Pencil className="mr-2 h-4 w-4" /> Modifica destinazione</DropdownMenuItem>
                    {q.dynamic && <DropdownMenuItem onClick={() => navigate(`/app/qr/${q.id}/statistiche`)}><BarChart3 className="mr-2 h-4 w-4" /> Statistiche</DropdownMenuItem>}
                    <DropdownMenuItem onClick={() => downloadPng(q)}><Download className="mr-2 h-4 w-4" /> Scarica PNG</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => downloadSvg(q)}><Download className="mr-2 h-4 w-4" /> Scarica SVG (stampa)</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => dup(q)}><Copy className="mr-2 h-4 w-4" /> Duplica</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toggle(q)}>{q.status === "active" ? <><Pause className="mr-2 h-4 w-4" /> Metti in pausa</> : <><Play className="mr-2 h-4 w-4" /> Riattiva</>}</DropdownMenuItem>
                    <DropdownMenuItem className="text-destructive" onClick={() => del(q)}><Trash2 className="mr-2 h-4 w-4" /> Archivia</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <p className="font-semibold truncate">{q.name}</p>
                {q.dynamic ? <Badge variant="secondary" className="text-[10px] gap-1"><RefreshCw className="h-3 w-3" /> Dinamico</Badge> : <Badge variant="outline" className="text-[10px]">Statico</Badge>}
                {q.status === "paused" && <Badge variant="outline" className="text-[10px] text-muted-foreground">In pausa</Badge>}
              </div>
              {q.dynamic ? (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground" data-testid={`qr-scans-${q.id}`}>{q.scans || 0} scansioni</p>
                  <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs font-semibold" onClick={() => navigate(`/app/qr/${q.id}/statistiche`)} data-testid={`qr-stats-${q.id}`}><BarChart3 className="h-3.5 w-3.5" /> Statistiche</Button>
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-muted-foreground leading-snug" data-testid={`qr-static-note-${q.id}`}>Le scansioni non possono essere rilevate: il QR apre direttamente la destinazione.</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* editor */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editor?.id ? "Modifica QR" : "Nuovo QR Code"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div><Label className="text-sm">Nome</Label><Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} className="h-10 mt-1" placeholder="Es. QR Tavoli" /></div>
              {!editor.id && (
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div><p className="text-sm font-medium">QR dinamico</p><p className="text-xs text-muted-foreground">Modificabile e tracciato</p></div>
                  <Switch checked={editor.dynamic} onCheckedChange={(v) => setEditor({ ...editor, dynamic: v })} />
                </div>
              )}
              <div>
                <Label className="text-sm">Destinazione</Label>
                <Select value={editor.target_type} onValueChange={(v) => setEditor({ ...editor, target_type: v, target_value: "" })}>
                  <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TARGETS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editor.target_type === "url" && <Input value={editor.target_value} onChange={(e) => setEditor({ ...editor, target_value: e.target.value })} placeholder="https://..." className="h-10" />}
              {editor.target_type === "page" && (
                <Select value={editor.target_value} onValueChange={(v) => setEditor({ ...editor, target_value: v })}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Scegli una pagina" /></SelectTrigger>
                  <SelectContent>{pages.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {editor.target_type === "menu" && (
                <Select value={editor.target_value} onValueChange={(v) => setEditor({ ...editor, target_value: v })}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Scegli un menù" /></SelectTrigger>
                  <SelectContent>{menus.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <div>
                <Label className="text-sm">Colore</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {COLOR_PRESETS.map((p) => (
                    <button key={p.id} onClick={() => setEditor({ ...editor, style: { fg: p.fg, bg: p.bg } })}
                      className={`h-9 w-9 rounded-lg border-2 ${editor.style?.fg === p.fg ? "border-primary ring-2 ring-primary/30" : "border-border"}`} style={{ background: p.bg }}>
                      <span className="block h-full w-full" style={{ background: p.fg, clipPath: "polygon(0 0,55% 0,0 55%)" }} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-center py-2"><QRPreview value={editor.dynamic ? "https://qora.app/r/anteprima" : (editor.target_value || "https://qora.app")} fgColor={editor.style?.fg} bgColor={editor.style?.bg} size={120} frame="none" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditor(null)}>Annulla</Button><Button className="btn-grad text-white font-semibold" onClick={save}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* analytics moved to /app/qr/:id/statistiche */}
    </div>
  );
}
